import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {CustomValidators} from '@core/validator/custom-validators';
import {Observable} from 'rxjs/Observable';
import {ValidatorService} from '@core/validator/validator.service';
import {JobOrderService} from '@core/ecv-http-services/services/job-order.service';
import {DictModel} from '../../dictionary/dict.model';
import {RankingService} from '@core/ecv-http-services/services/ranking.service';
import {DictEnum} from '../../enums/dict.enum';
import {DictService} from '@core/ecv-http-services/services/dict.service';
import {EcvLanguageService} from '@core/ecv-language.service';
import {Ng4LoadingSpinnerService} from 'ng4-loading-spinner';
import {SearchRankingModel} from '../search-ranking.model';
import {ActivatedRoute} from '@angular/router';
import {CompetenceExperienceModel} from '../competence-experience.model';

@Component({
  selector: 'app-ranking-search',
  templateUrl: './ranking-search.component.html'
})
export class RankingSearchComponent implements OnInit {
  jobOrderForm: FormGroup;
  skillSearchForm: FormGroup;
  cityForm: FormGroup;
  additionalForm: FormGroup;
  yearsOfExperienceForm: FormGroup;
  DictEnum = DictEnum;

  jobOrderNumber: string;
  jobOrderDescription: string;
  skills: DictModel[] = [];
  yearsOfExperience: number[] = [];
  cities: DictModel[] = [];
  salaryFrom: number;
  salaryTo: number;
  remoteWork: boolean;

  currentSkill: DictModel;
  currentYearsOFExperience: number;
  currentCity: DictModel;

  showNoCompetencesError = false;

  searchModel: SearchRankingModel;

  @Output() expertRef = new EventEmitter();

  constructor(private validatorService: ValidatorService,
              private jobOrderService: JobOrderService,
              private rankingService: RankingService,
              private dictService: DictService,
              private langService: EcvLanguageService,
              private spinnerService: Ng4LoadingSpinnerService,
              private route: ActivatedRoute) {
  }

  ngOnInit() {
    this.createForm();
    this.prefillJobOrderNumber();
  }

  private createForm() {
    this.jobOrderForm = new FormGroup({
      'jobOrderNumber': new FormControl('', [CustomValidators.validateJobOrderNumber])
    });
    this.skillSearchForm = new FormGroup({
      'skill-search': new FormControl(''),
      'skill-search-id': new FormControl(null, CustomValidators.validateDictExists),
      'yearsOfExperience': new FormControl('', [Validators.max(100), Validators.min(0),
        CustomValidators.validateYears])
    });
    this.cityForm = new FormGroup({
      'city-search': new FormControl(''),
      'city-search-id': new FormControl(null, CustomValidators.validateDictExists)
    });

    this.additionalForm = new FormGroup({
      'salaryFrom': new FormControl(null, [
        Validators.max(1000), Validators.min(0), CustomValidators.validateDecimal]),
      'salaryTo': new FormControl(null, [
        Validators.max(1000), Validators.min(0), CustomValidators.validateDecimal]),
      'remoteWork': new FormControl('')
    });
    this.yearsOfExperienceForm = new FormGroup({});
  }

  loadData(pageNumber: number, pageSize: number, newQuery: boolean, searchByText: boolean): Observable<any> {
    if (!this.validateSkillSearchForm()) {
      return;
    }

    if (newQuery) {
      let description = this.jobOrderDescription;
      if (!description) {
        description = this.skills.map(competence => competence.description).join(' ')
          .concat(' ', this.cities.map(competence => competence.description).join(' '));
      }

      let competenceExperiences = [];
      for (let i = 0; i < this.skills.length; i++) {
        competenceExperiences.push(new CompetenceExperienceModel(this.skills[i].id,
          this.yearsOfExperience[i] ? parseFloat(this.yearsOfExperience[i].toString().replace(',', '.')) : null));
      }
      this.searchModel = new SearchRankingModel(description, this.cities.map(city => city.id), competenceExperiences,
        this.salaryFrom, this.salaryTo, this.remoteWork);
    }

    if (searchByText) {
      return this.rankingService.getRankingByCvs(this.searchModel);
    }

    return this.rankingService.getRankingByKeys(this.searchModel);
  }

  validateSkillSearchForm() {
    this.validatorService.validate(this.skillSearchForm);
    this.validatorService.validate(this.yearsOfExperienceForm);
    if (!this.skills || this.skills.length === 0) {
      this.showNoCompetencesError = true;
      return false;
    }
    return this.validatorService.isValid(this.skillSearchForm) && this.validatorService.isValid(this.yearsOfExperienceForm);
  }

  searchSkillsForJobOrder() {
    this.validatorService.validate(this.jobOrderForm);
    if (!this.jobOrderNumber || !this.validatorService.isValid(this.jobOrderForm)) {
      return;
    }
    this.spinnerService.show();
    this.skills = [];
    this.cities = [];
    this.jobOrderDescription = null;

    this.jobOrderService.getJobOrderRequiredSkillsDescription(this.jobOrderNumber).subscribe((description) => {
      const jobOrderSkills = this.jobOrderService.getJobOrderSkills(this.jobOrderNumber);
      const jobOrderCities = this.jobOrderService.getJobOrderCities(this.jobOrderNumber);

      Observable.forkJoin([jobOrderSkills, jobOrderCities]).subscribe(results => {
        this.jobOrderDescription = description.value;
        this.skills = results[0];
        this.yearsOfExperience = new Array<number>(this.skills.length);
        this.cities = results[1];
        if (this.skills && this.skills.length > 0) {
          this.hideNoCompetencesError();
        }
        this.spinnerService.hide();
      }, errors => {
        this.jobOrderDescription = description.value;
        this.spinnerService.hide();
      });
    }, requestError => {
      this.spinnerService.hide();
    });
  }

  addSkill() {
    this.validatorService.validate(this.skillSearchForm);
    if (!this.validatorService.isValid(this.skillSearchForm)) {
      return;
    }
    if (this.currentSkill && !(this.skills.map(competence => competence.id).lastIndexOf(this.currentSkill.id) > -1)) {
      this.skills.unshift(this.currentSkill);
      this.yearsOfExperience.unshift(this.currentYearsOFExperience);
      this.currentSkill = undefined;
      this.currentYearsOFExperience = undefined;
      this.hideNoCompetencesError();
    }
  }

  addCity() {
    this.validatorService.validate(this.cityForm);
    if (this.currentCity && !(this.cities.map(city => city.id).lastIndexOf(this.currentCity.id) > -1)) {
      this.cities.unshift(this.currentCity);
      this.currentCity = undefined;
    }
  }

  removeSkill(index: number) {
    this.skills.splice(index, 1);
    this.yearsOfExperience.splice(index, 1);
  }

  removeCity(index: number) {
    this.cities.splice(index, 1);
  }

  getDescription(model: DictModel): string {
    return this.langService.pickFromDictionary(model);
  }

  currentSkillChanged(event) {
    this.currentSkill = event;
  }

  currentCityChanged(event) {
    this.currentCity = event;
  }

  getCurrentSkillId() {
    return this.currentSkill ? this.currentSkill.id : undefined;
  }

  getCurrentCityId() {
    return this.currentCity ? this.currentCity.id : undefined;
  }

  hideNoCompetencesError() {
    this.showNoCompetencesError = false;
  }

  private prefillJobOrderNumber() {
    this.route.params.subscribe(params => {
      const jobOrderNumberFromUrl = params['jobOrderNumber'];
      if (jobOrderNumberFromUrl !== undefined) {
        this.jobOrderNumber = jobOrderNumberFromUrl;
        this.searchSkillsForJobOrder();
      }
    });
  }
}



// WEBPACK FOOTER //
// C:/Users/Sylwia/Desktop/12.02/ecv_manager_frontend/src/app/ranking/ranking-search/ranking-search.component.ts